import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CheckCircle,
  XCircle,
  Clock,
  Calendar,
  Award,
  AlertCircle,
  ChevronRight,
  FileText,
  MessageSquare,
  BarChart3,
} from 'lucide-react';
import { attemptsApi } from '@/api/endpoints';
import { ExamAttempt } from '@/types';
import { format } from 'date-fns';
import { formatExamDateTime } from '@/utils';
import { StatCard } from '@azyntor-labs/nexus-ui';

export const ExamResults = () => {
  const { attemptId } = useParams<{ attemptId?: string }>();
  const navigate = useNavigate();
  const [selectedAttempt, setSelectedAttempt] = useState<ExamAttempt | null>(null);

  // Fetch student's exam attempts
  const { data: attemptsResponse, isLoading, isError } = useQuery({
    queryKey: ['my-attempts'],
    queryFn: () => attemptsApi.listMy({ page: 1, per_page: 100 }),
  });

  const attempts = Array.isArray(attemptsResponse?.data?.data?.data)
    ? attemptsResponse.data.data.data
    : Array.isArray(attemptsResponse?.data?.data)
    ? attemptsResponse.data.data
    : [];

  // Handle URL parameter to show specific attempt
  useEffect(() => {
    if (attemptId && attempts.length > 0) {
      const attempt = attempts.find((a) => a.id === attemptId);
      if (attempt && attempt.status === 'submitted') {
        setSelectedAttempt(attempt);
      }
    }
  }, [attemptId, attempts]);

  // Fetch detailed result when selected
  const { data: resultResponse, isError: isResultError } = useQuery({
    queryKey: ['attempt-result', selectedAttempt?.id],
    queryFn: () => attemptsApi.getResult(selectedAttempt?.id ?? ''),
    enabled: !!selectedAttempt,
  });

  const resultData = resultResponse?.data.data as any;
  const result = resultData?.attempt;
  const answers = resultData?.answers || [];
  const showPerQuestionScore = resultData?.show_per_question_score ?? true;
  const examTotalMarks = resultData?.exam_total_marks || answers.reduce((sum: number, a: any) => sum + (a.max_score || 0), 0) || 0;
  const examPassingMarks = resultData?.exam_passing_marks || 0;
  const stats = {
    totalQuestions: resultData?.total_questions || 0,
    correctAnswers: resultData?.correct_answers || 0,
    wrongAnswers: resultData?.wrong_answers || 0,
    timeTaken: resultData?.time_taken || 0,
  };

  // Calculate statistics - separate graded from pending
  const gradedAttempts = attempts.filter((a) => a.status === 'submitted' && a.score !== null && a.score !== undefined);
  const averageScore =
    gradedAttempts.length > 0
      ? gradedAttempts.reduce((sum, a) => sum + (a.percentage || 0), 0) /
        gradedAttempts.length
      : 0;
  const passedCount = gradedAttempts.filter((a) => a.result === 'pass').length;
  const passRate =
    gradedAttempts.length > 0 ? (passedCount / gradedAttempts.length) * 100 : 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="spinner" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-red-500">
        <AlertCircle className="w-12 h-12 mb-4" />
        <p className="text-lg font-medium">Failed to load exam results</p>
        <p className="text-sm text-gray-500 mt-2">Please try again later</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  // Detail view for individual exam result
  if (selectedAttempt) {
    // Check if exam is graded
    const isGraded = (selectedAttempt.score !== null && selectedAttempt.score !== undefined);
    const isPendingGrading = selectedAttempt.status === 'submitted' && !isGraded;

    // Show pending grading view if not yet graded
    if (isPendingGrading) {
      return (
        <div className="min-h-screen pb-20">
          <div className="glass-card rounded-none border-x-0 border-t-0">
            <div className="max-w-7xl mx-auto px-4 py-6">
              <button
                onClick={() => {
                  setSelectedAttempt(null);
                  navigate('/student/results');
                }}
                className="text-white/60 hover:text-white mb-4 transition-colors"
              >
                ← Back to Results
              </button>
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-white mb-2">Exam Submitted</h1>
                  <div className="flex items-center space-x-4 text-sm text-white/60">
                    {selectedAttempt.submit_time && (
                      <div className="flex items-center space-x-1">
                        <Calendar className="w-4 h-4" />
                        <span>{formatExamDateTime(selectedAttempt.submit_time)}</span>
                      </div>
                    )}
                    <div className="flex items-center space-x-1">
                      <Clock className="w-4 h-4" />
                      <span>{Math.floor((selectedAttempt.time_spent_seconds || 0) / 60)} minutes</span>
                    </div>
                  </div>
                </div>
                <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/30">
                  <Clock className="w-4 h-4" />
                  <span className="font-medium">Pending Grading</span>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto px-4 py-8">
            <div className="glass-card p-12 text-center">
              <div className="w-20 h-20 bg-amber-500/15 rounded-full flex items-center justify-center mx-auto mb-6">
                <Clock className="w-10 h-10 text-amber-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-3">Your Exam is Being Graded</h2>
              <p className="text-white/60 text-lg mb-6">
                Your exam has been submitted successfully and is awaiting grading by your instructor.
              </p>
              <div className="inline-flex items-start space-x-3 text-left bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4 max-w-md mx-auto mb-6">
                <AlertCircle className="w-5 h-5 text-cyan-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-cyan-400 font-medium mb-1">What happens next?</p>
                  <p className="text-sm text-cyan-400/80">
                    Your instructor will review your answers and provide a grade.
                    You'll receive a notification when your results are ready to view.
                  </p>
                </div>
              </div>

              {selectedAttempt.violations && selectedAttempt.violations.length > 0 && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-6 max-w-md mx-auto text-left">
                  <div className="flex items-start space-x-3">
                    <AlertCircle className="w-5 h-5 text-red-400 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-red-400 mb-2">
                        Proctoring Violations Detected
                      </h3>
                      <p className="text-sm text-red-400/80 mb-3">
                        {selectedAttempt.violations.length} violation(s) were recorded during this exam.
                      </p>
                      <ul className="space-y-1">
                        {selectedAttempt.violations.slice(0, 5).map((v: any, idx: number) => (
                          <li key={idx} className="text-sm text-red-400/70">
                            • {v.type?.replace('_', ' ')}: {v.description || v.details?.action}
                          </li>
                        ))}
                        {selectedAttempt.violations.length > 5 && (
                          <li className="text-sm text-red-400/60 italic">
                            ... and {selectedAttempt.violations.length - 5} more
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    // If the result query failed, show error
    if (isResultError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[400px] text-red-500">
          <AlertCircle className="w-12 h-12 mb-4" />
          <p className="text-lg font-medium">Failed to load exam result details</p>
          <p className="text-sm text-gray-500 mt-2">Please try again later</p>
          <button
            onClick={() => {
              setSelectedAttempt(null);
              navigate('/student/results');
            }}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Results
          </button>
        </div>
      );
    }

    // If not pending and no result data yet, show loading
    if (!result) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="spinner" />
        </div>
      );
    }

    // Show full graded results view
    const hasPenalty = (result.violation_count > 0 || result.violation_penalty_total > 0);

    return (
      <div className="min-h-screen pb-20">
        {/* Header */}
        <div className="max-w-7xl mx-auto px-4 pt-6">
          <button
            onClick={() => {
              setSelectedAttempt(null);
              navigate('/student/results');
            }}
            className="text-white/60 hover:text-white mb-4 transition-colors text-sm"
          >
            ← Back to Results
          </button>

          {/* Result Card */}
          <div className="glass-card p-6 mb-6">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h1 className="text-2xl font-bold text-white mb-2">
                  {resultData?.exam_title || 'Exam Results'}
                </h1>
                <div className="flex items-center space-x-4 text-sm text-white/50">
                  <div className="flex items-center space-x-1">
                    <Calendar className="w-4 h-4" />
                    <span>{result.submit_time && formatExamDateTime(result.submit_time)}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Clock className="w-4 h-4" />
                    <span>{Math.floor((stats.timeTaken || result?.time_spent_seconds || 0) / 60)} min</span>
                  </div>
                </div>
              </div>

              <div className="text-right">
                <div className="text-4xl font-bold text-white mb-1">
                  {result?.percentage?.toFixed(1)}%
                </div>
                <div
                  className={`inline-flex items-center space-x-1.5 px-3 py-1 rounded-full text-sm font-medium ${
                    (result?.result === 'pass' || result?.passed)
                      ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                      : 'bg-red-500/15 text-red-400 border border-red-500/30'
                  }`}
                >
                  {(result?.result === 'pass' || result?.passed) ? (
                    <><CheckCircle className="w-4 h-4" /><span>Passed</span></>
                  ) : (
                    <><XCircle className="w-4 h-4" /><span>Failed</span></>
                  )}
                </div>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-teal-500/10 border border-teal-500/20 rounded-lg p-4">
                <div className="text-xs text-teal-400 mb-1">Final Score</div>
                <div className="text-2xl font-bold text-white">{result?.score || 0} <span className="text-sm font-normal text-white/40">/ {examTotalMarks}</span></div>
                {examPassingMarks > 0 && <div className="text-xs text-white/30 mt-1">Passing: {examPassingMarks}</div>}
              </div>
              <div className="bg-white/[0.03] border border-white/[0.06] rounded-lg p-4">
                <div className="text-xs text-white/50 mb-1">Questions</div>
                <div className="text-2xl font-bold text-white">{stats.totalQuestions}</div>
              </div>
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4">
                <div className="text-xs text-emerald-400 mb-1">Correct</div>
                <div className="text-2xl font-bold text-white">{stats.correctAnswers}</div>
              </div>
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                <div className="text-xs text-red-400 mb-1">Wrong</div>
                <div className="text-2xl font-bold text-white">{stats.wrongAnswers}</div>
              </div>
            </div>
          </div>

          {/* Violation Penalty Breakdown */}
          {hasPenalty && (
            <div className="glass-card p-6 mb-6 border-orange-500/20">
              <h3 className="font-semibold text-orange-400 mb-4 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                Violation Penalty
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-white/5 border border-white/[0.06] rounded-lg p-3">
                  <div className="text-xs text-white/40">Violations</div>
                  <div className="text-xl font-bold text-white">{result.violation_count || 0}</div>
                </div>
                {result.score_before_penalty != null && (
                  <div className="bg-white/5 border border-white/[0.06] rounded-lg p-3">
                    <div className="text-xs text-white/40">Before Penalty</div>
                    <div className="text-xl font-bold text-white">{result.score_before_penalty} <span className="text-sm font-normal text-white/30">/ {examTotalMarks}</span></div>
                  </div>
                )}
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                  <div className="text-xs text-red-400">Deducted</div>
                  <div className="text-xl font-bold text-red-400">-{result.violation_penalty_total || 0} <span className="text-sm font-normal">({result.violation_penalty_percent || 0}%)</span></div>
                </div>
                {result.score_after_penalty != null && (
                  <div className="bg-teal-500/10 border border-teal-500/20 rounded-lg p-3">
                    <div className="text-xs text-teal-400">After Penalty</div>
                    <div className="text-xl font-bold text-teal-400">{result.score_after_penalty} <span className="text-sm font-normal text-white/30">/ {examTotalMarks}</span></div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Question breakdown */}
        <div className="max-w-7xl mx-auto px-4 py-8">
          <h2 className="text-xl font-bold text-white mb-4">Question Breakdown</h2>

          <div className="space-y-4">
            {answers.map((answer: any, index: number) => (
              <div key={answer.question_id} className="glass-card p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start space-x-3">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        !showPerQuestionScore
                          ? 'bg-white/10 text-white/40 border border-white/20'
                          : answer.is_correct
                          ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                          : 'bg-red-500/15 text-red-400 border border-red-500/30'
                      }`}
                    >
                      {!showPerQuestionScore ? (
                        <FileText className="w-5 h-5" />
                      ) : answer.is_correct ? (
                        <CheckCircle className="w-5 h-5" />
                      ) : (
                        <XCircle className="w-5 h-5" />
                      )}
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">
                        Question {index + 1}
                      </h3>
                      {answer.question_text ? (
                        <p className="text-sm text-white/70 mt-1">{answer.question_text}</p>
                      ) : (
                        <p className="text-sm text-white/40 mt-1">Question ID: {answer.question_id}</p>
                      )}
                    </div>
                  </div>

                  {showPerQuestionScore && (
                    <div className="text-right">
                      <div className="text-lg font-semibold text-white">
                        {answer.score || 0} / {answer.max_score || 1}
                      </div>
                      <div className="text-sm text-white/60">points</div>
                    </div>
                  )}
                </div>

                {/* Your answer */}
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-white/70 mb-2">
                    Your Answer:
                  </h4>
                  <div
                    className={`p-4 rounded-lg border-2 ${
                      !showPerQuestionScore
                        ? 'bg-white/5 border-white/[0.08]'
                        : answer.is_correct
                        ? 'bg-emerald-500/10 border-emerald-500/30'
                        : 'bg-red-500/10 border-red-500/30'
                    }`}
                  >
                    {answer.code_answer ? (
                      <p className="text-white whitespace-pre-wrap font-mono text-sm">
                        {answer.code_answer}
                      </p>
                    ) : (
                      <div className="space-y-1">
                        {(answer.answer_display || answer.answer || []).map((ansText: string, idx: number) => (
                          <div key={idx} className="text-white">
                            • {ansText}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Correct answer & explanation for wrong answers */}
                {!answer.is_correct && answer.correct_answer && answer.correct_answer.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-white/70 mb-2">Correct Answer:</h4>
                    <div className="p-4 rounded-lg border-2 bg-emerald-500/5 border-emerald-500/20">
                      <div className="space-y-1">
                        {answer.correct_answer.map((ans: string, idx: number) => (
                          <div key={idx} className="text-emerald-400">• {ans}</div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Explanation */}
                {answer.explanation && (
                  <div className="mb-4 bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4">
                    <div className="flex items-start space-x-2">
                      <MessageSquare className="w-5 h-5 text-cyan-400 mt-0.5" />
                      <div>
                        <h4 className="text-sm font-semibold text-cyan-400">Explanation</h4>
                        <p className="text-sm text-cyan-400/80 mt-1">{answer.explanation}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Instructor feedback (only if actually provided) */}
                {answer.feedback && (
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                    <div className="flex items-start space-x-2">
                      <MessageSquare className="w-5 h-5 text-amber-400 mt-0.5" />
                      <div>
                        <h4 className="text-sm font-semibold text-amber-400">Instructor Feedback</h4>
                        <p className="text-sm text-amber-400/80 mt-1">{answer.feedback}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // List view of all exam results
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">My Exam Results</h1>
        <p className="text-white/60">View your exam scores and performance history</p>
      </div>

      {/* Statistics cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-8">
        <StatCard
          title="Average Score"
          value={`${averageScore.toFixed(1)}%`}
          icon={<BarChart3 />}
          trend={averageScore >= 70 ? "up" : "down"}
          trendValue={`${averageScore.toFixed(1)}%`}
          color={averageScore >= 70 ? 'green' : 'red'}
        />
        <StatCard
          title="Exams Passed"
          value={`${passedCount}/${gradedAttempts.length}`}
          icon={<Award />}
          trend={passRate >= 50 ? "up" : "down"}
          trendValue={`${passRate.toFixed(0)}%`}
          color="teal"
        />
        <StatCard
          title="Completed Exams"
          value={gradedAttempts.length}
          icon={<FileText />}
          trend={gradedAttempts.length > 0 ? "up" : "neutral"}
          trendValue={`${gradedAttempts.length} total`}
          color="teal"
        />
      </div>

      {/* Exam results list */}
      {attempts.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <FileText className="w-16 h-16 text-white/30 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">No Exam Results</h3>
          <p className="text-white/60">
            You haven't completed any exams yet. Results will appear here once you submit
            an exam.
          </p>
        </div>
      ) : (
        <div className="glass-card overflow-hidden">
          <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-white/[0.06]">
            <h2 className="text-base sm:text-lg font-semibold text-white">All Exam Results</h2>
          </div>

          <div className="divide-y divide-white/[0.06]">
            {attempts.map((attempt) => {
              const submittedDate = attempt.submit_time || attempt.end_time || attempt.updated_at
                ? new Date(attempt.submit_time || attempt.end_time || attempt.updated_at)
                : null;

              return (
                <div
                  key={attempt.id}
                  className="p-4 sm:p-6 hover:bg-white/[0.02] transition-colors cursor-pointer"
                  onClick={() => {
                    if (attempt.status === 'submitted') {
                      setSelectedAttempt(attempt);
                      navigate(`/student/results/${attempt.id}`);
                    }
                  }}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h3 className="text-base sm:text-lg font-semibold text-white truncate">
                          {attempt.exam_title || attempt.exam_id}
                        </h3>
                        <span
                          className={`px-2 py-0.5 text-xs font-medium rounded border shrink-0 ${
                            (attempt.score !== null && attempt.score !== undefined)
                              ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                              : attempt.status === 'submitted'
                              ? 'bg-amber-500/15 text-amber-400 border-amber-500/30'
                              : attempt.status === 'terminated'
                              ? 'bg-red-500/15 text-red-400 border-red-500/30'
                              : 'bg-cyan-500/15 text-cyan-400 border-cyan-500/30'
                          }`}
                        >
                          {(attempt.score !== null && attempt.score !== undefined)
                            ? 'Graded'
                            : attempt.status === 'submitted'
                            ? 'Pending Grading'
                            : attempt.status === 'terminated'
                            ? 'Terminated'
                            : 'In Progress'}
                        </span>
                        {(attempt.score !== null && attempt.score !== undefined) && (attempt.result || attempt.passed !== null) && (
                          <span
                            className={`px-2 py-0.5 text-xs font-medium rounded border shrink-0 ${
                              (attempt.result === 'pass' || attempt.passed)
                                ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                                : 'bg-red-500/15 text-red-400 border-red-500/30'
                            }`}
                          >
                            {(attempt.result === 'pass' || attempt.passed) ? 'Passed' : 'Failed'}
                          </span>
                        )}
                      </div>

                      <div className="flex flex-wrap items-center gap-3 sm:gap-6 text-xs sm:text-sm text-white/60">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          <span>
                            {submittedDate ? format(submittedDate, 'PP') : 'N/A'}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          <span>{Math.floor((attempt.time_spent_seconds || 0) / 60)} min</span>
                        </div>
                        <div className={`flex items-center gap-1 ${(attempt.violation_count || attempt.violations?.length || 0) > 0 ? 'text-red-400' : 'text-white/40'}`}>
                          <AlertCircle className="w-3.5 h-3.5" />
                          <span>{attempt.violation_count || attempt.violations?.length || 0} violation{(attempt.violation_count || attempt.violations?.length || 0) !== 1 ? 's' : ''}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between sm:justify-end gap-4 sm:gap-6">
                      {(attempt.score !== null && attempt.score !== undefined) && (
                        <div className="sm:text-right">
                          <div className="text-xl sm:text-2xl font-bold text-white">
                            {attempt.percentage?.toFixed(1)}%
                          </div>
                          <div className="text-xs sm:text-sm text-white/60">
                            {attempt.score} points
                          </div>
                        </div>
                      )}
                      {(attempt.score !== null && attempt.score !== undefined) && (
                        <ChevronRight className="w-5 h-5 text-white/40 hidden sm:block" />
                      )}
                      {attempt.status === 'terminated' && (
                        <div className="text-sm text-red-400 font-medium">
                          Terminated
                        </div>
                      )}
                      {attempt.status === 'submitted' && (attempt.score === null || attempt.score === undefined) && (
                        <div className="text-sm text-amber-400 font-medium">
                          Awaiting Grade
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
